import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  List, DollarSign, Eye, Calendar, Target, Clock, Zap,
  Plus, ChevronRight, Sparkles, Timer, CheckCircle2,
  Tag, Check, X, Settings, Trash2, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import type { Task } from '../../services/productivity/types';

interface MorningDashboardProps {
  tasks: Task[];
  dailyLog: any;
  onTaskCreate: (task: Omit<Task, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => void;
  onTaskUpdate: (id: string, task: Partial<Task>) => void;
  onTaskDelete: (id: string) => void;
  onTaskStatusChange: (id: string, status: 'todo' | 'in_progress' | 'done') => void;
}

const AVAILABLE_TAGS = [
  { id: 'prioridade', color: 'bg-red-400/10 text-red-400' },
  { id: 'médio', color: 'bg-amber-400/10 text-amber-400' },
  { id: 'baixo', color: 'bg-green-400/10 text-green-400' },
  { id: 'pessoal', color: 'bg-purple-400/10 text-purple-400' },
  { id: 'trabalho', color: 'bg-blue-400/10 text-blue-400' },
  { id: 'delegar', color: 'bg-pink-400/10 text-pink-400' },
  { id: 'LTV', color: 'bg-indigo-400/10 text-indigo-400' },
  { id: 'importante', color: 'bg-orange-400/10 text-orange-400' },
  { id: 'urgente', color: 'bg-rose-400/10 text-rose-400' },
  { id: 'estudos', color: 'bg-teal-400/10 text-teal-400' },
  { id: 'saúde', color: 'bg-emerald-400/10 text-emerald-400' },
  { id: 'finanças', color: 'bg-lime-400/10 text-lime-400' },
] as const;

const MorningDashboard: React.FC<MorningDashboardProps> = ({
  tasks,
  dailyLog,
  onTaskCreate,
  onTaskUpdate,
  onTaskDelete,
  onTaskStatusChange,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showAddTask, setShowAddTask] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedPriority, setSelectedPriority] = useState<Task['priority']>('medium');
  const [estimatedTime, setEstimatedTime] = useState<number | undefined>();
  const [dueDate, setDueDate] = useState<Date | undefined>();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleAddTask = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newTaskTitle.trim()) return;

    if (editingTask) {
      onTaskUpdate(editingTask.id, {
        title: newTaskTitle,
        description: newTaskDescription,
        tags: selectedTags,
        priority: selectedPriority,
        estimated_time: estimatedTime,
        due_date: dueDate,
      });
    } else {
      onTaskCreate({
        title: newTaskTitle,
        description: newTaskDescription,
        tags: selectedTags,
        priority: selectedPriority,
        status: 'todo',
        estimated_time: estimatedTime,
        due_date: dueDate,
      });
    }

    setNewTaskTitle('');
    setNewTaskDescription('');
    setSelectedTags([]);
    setSelectedPriority('medium');
    setEstimatedTime(undefined);
    setDueDate(undefined);
    setShowAddTask(false);
    setEditingTask(null);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setNewTaskTitle(task.title);
    setNewTaskDescription(task.description || '');
    setSelectedTags(task.tags || []);
    setSelectedPriority(task.priority);
    setEstimatedTime(task.estimated_time);
    setDueDate(task.due_date ? new Date(task.due_date) : undefined);
    setShowAddTask(true);
  };

  const handleToggleTaskStatus = async (taskId: string, currentStatus: string) => {
    try {
      if (onTaskStatusChange) {
        const newStatus = currentStatus === 'done' ? 'todo' : 'done';
        await onTaskStatusChange(taskId, newStatus);
        toast.success(newStatus === 'done' ? 'Tarefa concluída!' : 'Tarefa reaberta!');
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await onTaskDelete(taskId);
      toast.success('Tarefa excluída!');
    } catch (error) {
      console.error('Erro ao excluir tarefa:', error);
      toast.error('Erro ao excluir tarefa');
    }
  };

  const getTagStyle = (tagId: string) => {
    return AVAILABLE_TAGS.find(tag => tag.id === tagId)?.color || 'bg-zinc-700/50 text-zinc-400';
  };

  return (
    <div className="space-y-6">
      {/* Lista de Tarefas */}
      <div className="bg-[rgb(28,28,30)] rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[20px] font-semibold text-white">Tarefas</h2>
          <button
            onClick={() => {
              setShowAddTask(true);
              setEditingTask(null);
              setNewTaskTitle('');
              setNewTaskDescription('');
              setSelectedTags([]);
              setSelectedPriority('medium');
              setEstimatedTime(undefined);
              setDueDate(undefined);
            }}
            className="bg-white hover:bg-white/90 text-black rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Lista de Tarefas */}
        <div className="space-y-3">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="bg-[rgb(44,44,46)] hover:bg-[rgb(58,58,60)] rounded-2xl p-4 transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleToggleTaskStatus(task.id, task.status)}
                    className={`rounded-full p-1 transition-all duration-200 ${
                      task.status === 'done'
                        ? 'bg-white text-black'
                        : 'text-[rgb(152,152,157)] hover:text-white'
                    }`}
                  >
                    <CheckCircle2 className="w-5 h-5" />
                  </button>
                  <div>
                    <h3 className={`text-[17px] font-medium ${
                      task.status === 'done' ? 'line-through text-[rgb(152,152,157)]' : 'text-white'
                    }`}>
                      {task.title}
                    </h3>
                    {task.description && (
                      <p className="text-[13px] text-[rgb(152,152,157)]">{task.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {task.tags?.map((tag) => (
                    <span
                      key={tag}
                      className={`px-2 py-1 text-[13px] rounded-lg ${
                        AVAILABLE_TAGS.find((t) => t.id === tag)?.color || 'bg-[rgb(58,58,60)] text-[rgb(152,152,157)]'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                  <button
                    onClick={() => handleEditTask(task)}
                    className="p-2 text-[rgb(152,152,157)] hover:text-white rounded-lg transition-all duration-200"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    className="p-2 text-[rgb(152,152,157)] hover:text-red-400 rounded-lg transition-all duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Adicionar/Editar Tarefa */}
      {showAddTask && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-md z-[100]">
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <motion.div 
              className="w-full max-w-lg bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl rounded-2xl p-6 space-y-6 border border-[rgb(44,44,46)] shadow-2xl"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-[20px] font-semibold text-white">
                  {editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddTask(false);
                    setEditingTask(null);
                    setNewTaskTitle('');
                    setNewTaskDescription('');
                    setSelectedTags([]);
                    setSelectedPriority('medium');
                    setEstimatedTime(undefined);
                    setDueDate(undefined);
                  }}
                  className="p-2 text-[rgb(152,152,157)] hover:text-white rounded-lg transition-all duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleAddTask} className="space-y-6">
                <div>
                  <label className="block text-[15px] font-medium text-[rgb(152,152,157)] mb-2">
                    Título
                  </label>
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    className="w-full px-4 py-3 text-[17px] bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl rounded-2xl border border-[rgb(44,44,46)] focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200 placeholder-[rgb(152,152,157)]"
                    placeholder="Digite o título da tarefa"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[15px] font-medium text-[rgb(152,152,157)] mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={newTaskDescription}
                    onChange={(e) => setNewTaskDescription(e.target.value)}
                    className="w-full px-4 py-3 text-[17px] bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl rounded-2xl border border-[rgb(44,44,46)] focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200 placeholder-[rgb(152,152,157)]"
                    placeholder="Digite uma descrição (opcional)"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-[15px] font-medium text-[rgb(152,152,157)] mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_TAGS.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          if (selectedTags.includes(tag.id)) {
                            setSelectedTags(selectedTags.filter((t) => t !== tag.id));
                          } else {
                            setSelectedTags([...selectedTags, tag.id]);
                          }
                        }}
                        className={`px-4 py-2 text-[15px] font-medium rounded-2xl transition-all duration-200 ${
                          selectedTags.includes(tag.id)
                            ? tag.color
                            : 'bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:text-white'
                        }`}
                      >
                        {tag.id}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[15px] font-medium text-[rgb(152,152,157)] mb-2">
                    Prioridade
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'low', label: 'Baixa', color: 'bg-green-400/10 text-green-400' },
                      { id: 'medium', label: 'Média', color: 'bg-amber-400/10 text-amber-400' },
                      { id: 'high', label: 'Alta', color: 'bg-red-400/10 text-red-400' }
                    ].map((priority) => (
                      <button
                        key={priority.id}
                        type="button"
                        onClick={() => setSelectedPriority(priority.id as Task['priority'])}
                        className={`px-4 py-2 text-[15px] font-medium rounded-2xl transition-all duration-200 ${
                          selectedPriority === priority.id
                            ? priority.color
                            : 'bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:text-white'
                        }`}
                      >
                        {priority.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[15px] font-medium text-[rgb(152,152,157)] mb-2">
                    Tempo estimado (minutos)
                  </label>
                  <input
                    type="number"
                    value={estimatedTime || ''}
                    onChange={(e) => setEstimatedTime(e.target.value ? Number(e.target.value) : undefined)}
                    className="w-full px-4 py-3 text-[17px] bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl rounded-2xl border border-[rgb(44,44,46)] focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200 placeholder-[rgb(152,152,157)]"
                    placeholder="Digite o tempo estimado"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-[15px] font-medium text-[rgb(152,152,157)] mb-2">
                    Data de entrega
                  </label>
                  <input
                    type="date"
                    value={dueDate ? new Date(dueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setDueDate(e.target.value ? new Date(e.target.value) : undefined)}
                    className="w-full px-4 py-3 text-[17px] bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl rounded-2xl border border-[rgb(44,44,46)] focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200 placeholder-[rgb(152,152,157)]"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddTask(false);
                      setEditingTask(null);
                      setNewTaskTitle('');
                      setNewTaskDescription('');
                      setSelectedTags([]);
                      setSelectedPriority('medium');
                      setEstimatedTime(undefined);
                      setDueDate(undefined);
                    }}
                    className="px-4 py-2 text-[15px] font-medium text-[rgb(152,152,157)] hover:text-white rounded-2xl transition-all duration-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="bg-white hover:bg-white/90 text-black rounded-2xl py-2 px-4 text-[15px] font-medium transition-all duration-200"
                  >
                    {editingTask ? 'Salvar' : 'Criar'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MorningDashboard;